<template>
  <div class="flex flex-col items-center" data-id="search-bar">
    <h2
      v-if="heading"
      class="text-shadow text-wrap:balance mb-2 text-4xl tracking-tight text-txt-contrast shadow-txt-weak md:self-start"
    >
      {{ heading }}
    </h2>
    <div
      class="grid w-full grid-cols-1 gap-2 rounded-lg sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-10"
      :class="showBackground ? 'bg-white/80 p-4 sm:p-6' : ''"
    >
      <WebccDestinationPicker
        ref="destinationPicker"
        v-model="selectedItem"
        :placeholder="$t(`${trPrefix}placeholders.where`)"
      />

      <div
        class="flex flex-col sm:col-span-2 md:col-span-1 lg:col-span-3"
        data-id="date-range-field"
      >
        <div
          class="flex flex-col"
          :class="{ relative: !flexTrip.flexibleSearch }"
        >
          <label class="relative w-full">
            <input
              ref="dateRangeInput"
              class="h-12 rounded border w-full border-bgr-300 px-8 placeholder:text-txt-weak focus:outline-none focus:ring-2 focus:ring-thm focus:ring-offset-2 md:px-7 lg:px-8"
              :placeholder="$t(`${trPrefix}placeholders.when`)"
              autocomplete="off"
              :value="flexTrip.tripText"
              readonly
              @focus="openFocusCalendar"
              @click.stop.prevent="openFocusCalendar"
              @keyup="onCalendarInputKeyUp"
            />
            <div class="pointer-events-none absolute left-2 top-[0.9rem]">
              <WebccIcon
                name="site/calendar"
                class="h-4 w-4 fill-current text-txt"
              />
            </div>
            <div
              v-if="flexTrip.tripText.length"
              class="absolute right-2 top-[0.9rem] cursor-pointer opacity-20 hover:opacity-60"
              @click.stop.prevent="reset"
            >
              <WebccIcon name="site/x" class="h-5 w-5 fill-current text-txt" />
            </div>
          </label>
          <WebccDaterangeSearch
            v-if="flexTrip.open"
            ref="dateRangeSearch"
            class="z-5"
            :class="
              flexTrip.flexibleSearch
                ? dateRangeFlexSearchClasses
                : 'left-1/2 right-1/2 top-12 md:top-14'
            "
            @close="onCalendarClose"
          />
        </div>
      </div>

      <label class="relative flex flex-col lg:col-span-2" data-id="pax-field">
        <select
          v-model="pax"
          class="h-12 rounded border border-bgr-300 pl-4 pr-8 text-txt focus:outline-none focus:ring-2 focus:ring-thm focus:ring-offset-2 pax-num"
          :class="{ 'text-txt-weak': pax === 0 }"
          @focus="flexTrip.open = false"
          @input="selectPax($event)"
        >
          <option class="padding-x-4" disabled selected hidden value="0">
            {{ $t(`${trPrefix}placeholders.pax`) }}
          </option>
          <option class="padding-x-4" :value="0"></option>
          <option
            v-for="i in 47"
            :key="i"
            class="padding-x-4 text-txt"
            :value="i"
          >
            {{ i }}
          </option>
        </select>
      </label>

      <WebccButton
        variant="cta"
        class="h-12 lg:col-span-2"
        @click="performSubmit"
      >
        {{ $t(`${trPrefix}search`) }}
      </WebccButton>

      <slot name="extraLine" />
    </div>
  </div>
</template>

<script setup lang="ts">
const trPrefix = 'www.components.elements.SearchBox.'

const props = withDefaults(
  defineProps<{
    locale: string
    heading?: string
    showBackground?: boolean
    searchQueryParams?: Record<string, string>
    dateRangeFlexSearchClasses?: string
  }>(),
  {
    heading: '',
    showBackground: true,
    searchQueryParams: undefined,
    dateRangeFlexSearchClasses:
      'md:top-[17rem] lg:top-[20.125rem] xl:top-[23.25rem] right-0 md:min-w-[73.75%] lg:min-w-[69%]',
  },
)

const confData = useConfdata()
const tracking = useTracking()
const params = useParams()
const partner = usePartner()
const flexTrip = useFlexTrip()

const dateRangeInput = ref<HTMLInputElement>()
const dateRangeSearch = ref()
const destinationPicker = ref()
const { focused: isDestinationFocused } = useFocusWithin(destinationPicker)
watch(isDestinationFocused, (isFocused) => {
  if (isFocused) flexTrip.open = false
})

const pax = ref(0)
const selectedItem = shallowRef<AutosuggestItem>()
watch(selectedItem, (newValue) => {
  if (!newValue) return

  openFocusCalendar()
  tracking.handlers?.qs.autosuggestItemSelected(newValue)
  tracking.handlers?.homepage.autosuggestItemSelected(newValue)
})

function onCalendarClose() {
  flexTrip.open = false
}

function reset() {
  flexTrip.fuzzyness = 0
  flexTrip.reset()
}

async function openFocusCalendar() {
  if (flexTrip.open) return

  flexTrip.open = true
  await nextTick()
  dateRangeInput.value?.blur()
  dateRangeSearch.value?.focusOnFirstDate()
}

async function performSubmit() {
  const checkin = flexTrip.range.start && toServerDate(flexTrip.range.start)
  const checkout = flexTrip.range.end && toServerDate(flexTrip.range.end)
  const partnerid = partner.id

  const presetQuery = props.searchQueryParams
  const duration = flexTrip.duration
    ? flexTrip.duration
    : checkin && checkout
      ? calculateDuration(checkin, checkout)
      : ''

  tracking.handlers?.qs.submitSearch({
    text: selectedItem.value?.label ?? '',
    date: checkin ?? '',
    duration: checkin && checkout ? calculateDuration(checkin, checkout) : '',
    pax: pax.value,
    ...presetQuery,
  })
  tracking.handlers?.homepage.submitSearch({
    text: selectedItem.value?.label ?? '',
    date: checkin ?? '',
    duration,
    pax: pax.value,
    ...presetQuery,
  })

  const query = {
    ...presetQuery,
    ...params.persistent,
    checkin,
    checkout,
    pax: pax.value > 0 ? pax.value : undefined,
    duration:
      flexTrip.flexibleSearch &&
      flexTrip.duration &&
      checkin &&
      flexTrip.fuzzyness === 0
        ? flexTrip.duration
        : undefined,
    fuzzyness: flexTrip.fuzzyness > 0 ? flexTrip.fuzzyness : undefined,
  }

  if (partnerid) {
    Object.assign(query, { partnerid })
  }

  const path = buildPath(
    confData.languagePrefix,
    selectedItem.value?.path ?? confData.prefixSearchpage,
  )

  await navigateTo({ path, query })
}

function onCalendarInputKeyUp({ key }: KeyboardEvent) {
  if (['Delete', 'Backspace'].includes(key)) reset()
}

function selectPax(event: Event) {
  tracking.handlers?.qs.numberPax((event.target as HTMLSelectElement).value)
  tracking.handlers?.homepage.numberPax(
    (event.target as HTMLSelectElement).value,
  )
}
</script>
